import { AlertTriangle, CircleX, RotateCcw, Warehouse, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn, TRUNCATE_SAFE } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { StockStatus } from '@features/inventory';
import { BADGE_TONE, StockStatusBadge } from './StockStatusBadge';
import { ShortfallPopover } from './ShortfallPopover';
import { compositionLabel, type ItemPlan, type PlannedLine } from './itemPlan';
import type { ItemPendingQuantityDto, SelectedItemDisplay } from '../types';

interface CartItemCardProps {
  plan: ItemPlan;
  /** Stock read state for the badge — loading and error are not knowable from the plan alone. */
  status: StockStatus;
  /** What other open picklists hold of this item, split as the badge's hover shows it; null until read. */
  pending: ItemPendingQuantityDto | null;
  binCodes: string[];
  /** How many bins the user explicitly chose for this item, or null while it is automatic. */
  chosenBinCount: number | null;
  canAllocateBins: boolean;
  onRemoveItem: (plan: ItemPlan) => void;
  onRemoveLine: (item: SelectedItemDisplay) => void;
  onQuantityChange: (item: SelectedItemDisplay, quantity: number) => void;
  onResetQuantity: (item: SelectedItemDisplay) => void;
  onOpenBins: (plan: ItemPlan) => void;
}

/** `dept / end use`, with the separator dropped when one side is missing. */
function purposeOf(item: SelectedItemDisplay): string {
  return [item.netsuiteDepartmentName, item.netsuiteEndUseName].filter(Boolean).join(' / ');
}

/**
 * One cart card per ITEM, with every MRS line visible beneath it (owner decision 1, 2026-09-18).
 *
 * The lines are not hidden behind an expander: two lines of one item are two different jobs, and they
 * are the thing this redesign exists to surface. Hiding them behind a click hides it again. The cost —
 * taller cards on a long cart — was accepted explicitly.
 */
export function CartItemCard({
  plan,
  status,
  pending,
  binCodes,
  chosenBinCount,
  canAllocateBins,
  onRemoveItem,
  onRemoveLine,
  onQuantityChange,
  onResetQuantity,
  onOpenBins,
}: CartItemCardProps) {
  const isOutOfStock = status === 'out_of_stock' && plan.known;
  const isShort = !isOutOfStock && plan.known && plan.shortfall > 0;
  const composition = compositionLabel(plan);
  const multiLine = plan.lines.length > 1;

  /**
   * The chip that REPLACES the stock badge when the picklist cannot cover this item.
   *
   * `null` means the ordinary stock badge stands. Tones are the prototype`s soft tints, never solid
   * fills — see BADGE_TONE.
   */
  const shortfallChip = isOutOfStock
    ? { label: "Out of Stock", tone: BADGE_TONE.destructive, Icon: CircleX }
    : isShort
      ? {
          label: `Short ${plan.shortfall} ${plan.unitOfMeasure}`,
          tone: BADGE_TONE.warning,
          Icon: AlertTriangle,
        }
      : null;

  const binsButton = canAllocateBins && !isOutOfStock && (
    <Button
      variant={chosenBinCount ? 'secondary' : 'ghost'}
      size="sm"
      className="h-6 shrink-0 gap-1 px-2 text-xs"
      onClick={() => onOpenBins(plan)}
      title={chosenBinCount ? `${chosenBinCount} bin(s) chosen` : 'Choose bins and serials'}
    >
      <Warehouse className="h-3 w-3" />
      {chosenBinCount ? `${chosenBinCount} bin(s)` : 'Bins'}
    </Button>
  );

  return (
    <div
      className={`rounded-md p-2 text-sm ${
        isOutOfStock
          ? 'border border-destructive/30 bg-destructive/[0.08]'
          : 'border border-transparent bg-muted/45'
      }`}
    >
      {/* Header — item identity, stock state, and the control that drops the whole item */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-xs">{plan.itemCode}</span>
            {/*
              ONE chip, as the prototype has it: the cart card shows In Stock OR Short N OR Out of
              Stock, never a stock badge with a short badge beside it. Short REPLACES the stock state
              rather than annotating it, because a card that says "In Stock" and "Short 3" in the same
              breath makes the reader decide which one to believe.
            */}
            {shortfallChip ? (
              <ShortfallPopover plan={plan}>
                <span
                  className={`inline-flex h-5 cursor-help items-center gap-1 whitespace-nowrap rounded-full border px-[7px] text-xs leading-none ${shortfallChip.tone}`}
                >
                  <shortfallChip.Icon className="h-3 w-3" />
                  {shortfallChip.label}
                </span>
              </ShortfallPopover>
            ) : (
              <StockStatusBadge
                status={status}
                quantityAvailable={plan.onHand ?? undefined}
                requestedQuantity={plan.wanted}
                binCodes={binCodes}
                pendingPickingQty={pending?.pendingPickingQuantity}
                pendingPostingQty={pending?.pendingPostingQuantity}
              />
            )}
          </div>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="line-clamp-2 w-fit text-xs text-muted-foreground">{plan.itemName}</p>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                <p className="text-xs">
                  <span className="font-mono">{plan.itemCode}</span> · {plan.itemName}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* The combined figure and what composes it — the point of grouping by item */}
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
            <span className="font-medium tabular-nums">
              {plan.total} {plan.unitOfMeasure}
            </span>
            {composition && <span className="text-muted-foreground">{composition}</span>}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {multiLine && binsButton}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => onRemoveItem(plan)}
            title="Remove item"
            aria-label={`Remove ${plan.itemCode} from the picklist`}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* A single-line item keeps the inline control row it has today, so it is no taller than before */}
      {!isOutOfStock && !multiLine && (
        <LineControls
          line={plan.lines[0]}
          unitOfMeasure={plan.unitOfMeasure}
          onQuantityChange={onQuantityChange}
          onResetQuantity={onResetQuantity}
          trailing={
            <>
              <span className="ml-auto truncate font-mono text-[11px] text-muted-foreground">
                {plan.lines[0].item.mrsNumber}
              </span>
              {binsButton}
            </>
          }
        />
      )}

      {/* Multi-line: every MRS line, always visible */}
      {!isOutOfStock && multiLine && (
        <ul className="mt-2 space-y-1">
          {plan.lines.map((line) => (
            <li
              key={line.key}
              className={`rounded-[7px] border border-border p-1.5 ${
                line.short ? 'bg-warning/10' : 'bg-card'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-1.5">
                    <span className="font-mono text-[11px]">{line.item.mrsNumber}</span>
                    {line.short && (
                      <span className="text-[10.5px] font-semibold text-foreground">
                        Short {line.shortBy} {plan.unitOfMeasure}
                      </span>
                    )}
                  </div>
                  {/* ⚠️ `text-[11px]` is an arbitrary size and so carries NO paired line-height —
                      `leading-normal` has to be stated outright on both of these. */}
                  <p className={cn(TRUNCATE_SAFE, 'text-[11px] leading-normal text-muted-foreground')}>
                    {purposeOf(line.item)}
                  </p>
                  {/* One line, full text on hover (owner ruling 4): every line is visible now, so a
                      two-line clamp here grows the cart fast — and the real cases diverge early. */}
                  {line.item.remarks && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className={cn(TRUNCATE_SAFE, 'w-fit max-w-full text-[11px] italic leading-normal text-muted-foreground')}>
                            {line.item.remarks}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm">
                          <p className="text-xs">{line.item.remarks}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 shrink-0 p-0"
                  onClick={() => onRemoveLine(line.item)}
                  title="Remove this MRS line"
                  aria-label={`Remove ${line.item.mrsNumber} from ${plan.itemCode}`}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>

              <LineControls
                line={line}
                unitOfMeasure={plan.unitOfMeasure}
                onQuantityChange={onQuantityChange}
                onResetQuantity={onResetQuantity}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface LineControlsProps {
  line: PlannedLine;
  unitOfMeasure: string;
  onQuantityChange: (item: SelectedItemDisplay, quantity: number) => void;
  onResetQuantity: (item: SelectedItemDisplay) => void;
  trailing?: React.ReactNode;
}

/**
 * The quantity field for one MRS line.
 *
 * `/ {wanted}` is what the line may STILL take (`availableQuantity`), not what the MRS originally asked
 * for — owner ruling 2026-09-18. A line that asked for 10 with 6 already issued reads `4 of 4`, so a
 * legitimately part-issued line never looks like a shortfall.
 */
function LineControls({
  line,
  unitOfMeasure,
  onQuantityChange,
  onResetQuantity,
  trailing,
}: LineControlsProps) {
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <Input
        type="number"
        value={line.pick}
        onChange={(e) => onQuantityChange(line.item, Number(e.target.value))}
        className={`h-7 w-16 text-center text-xs ${
          line.short ? 'border-warning' : line.overridden ? 'border-warning' : ''
        }`}
        min={0}
        max={line.cap}
        aria-label={`Quantity for ${line.item.mrsNumber}`}
      />
      <span className="text-xs text-muted-foreground">
        / {line.wanted} {unitOfMeasure}
      </span>
      {line.overridden && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={() => onResetQuantity(line.item)}
          title="Reset quantity"
          aria-label={`Reset quantity for ${line.item.mrsNumber}`}
        >
          <RotateCcw className="h-3 w-3" />
        </Button>
      )}
      {trailing}
    </div>
  );
}
